import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import ImageUpload from "./ImageUpload";
import { CalendarDays, Plus, Pencil, Trash2, Star, Megaphone, MapPin, Rocket, Check } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { key: "sagra", label: "Sagra" }, { key: "festa", label: "Festa patronale" },
  { key: "concerto", label: "Concerto" }, { key: "sport", label: "Evento sportivo" },
  { key: "mercatino", label: "Mercatino" }, { key: "inaugurazione", label: "Inaugurazione" },
  { key: "culturale", label: "Evento culturale" }, { key: "promo", label: "Promozione" },
  { key: "altro", label: "Altro" },
];
const CAT_LABEL = Object.fromEntries(CATS.map((c) => [c.key, c.label]));
const STATUS = { live: { t: "In corso", c: "text-[#00E676] bg-[#00E676]/15 border-[#00E676]/30" }, upcoming: { t: "In programma", c: "text-cyan-300 bg-cyan-500/15 border-cyan-500/30" }, ended: { t: "Terminato", c: "text-zinc-400 bg-zinc-500/15 border-zinc-500/30" } };

const EMPTY = {
  title: "", description: "", category: "sagra", start_at: "", end_at: "",
  location_name: "", address: "", comune: "", image: "",
  published: true, featured: false, sponsored: false, sponsor_name: "", priority: 0,
};
const toLocal = (iso) => (iso ? String(iso).slice(0, 16) : "");

export default function CeoEvents() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [plans, setPlans] = useState([]);
  const [sponsorFor, setSponsorFor] = useState(null);
  const [sponsorPlan, setSponsorPlan] = useState("");
  const [sponsorName, setSponsorName] = useState("");
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/ceo/events").then(({ data }) => setEvents(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/events/sponsor-plans").then(({ data }) => setPlans(data || [])).catch(() => {}); }, []);

  // Ritorno da Stripe: verifica il pagamento della sponsorizzazione e ricarica.
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("sponsor_session");
    if (!sid) return;
    api.get(`/payments/status/${sid}`).then(({ data }) => {
      if (data.payment_status === "paid") toast.success("Sponsorizzazione attivata! L'evento è ora in evidenza sulla mappa.");
      else toast("Pagamento non completato.");
    }).catch(() => {}).finally(() => {
      window.history.replaceState({}, "", window.location.pathname);
      load();
    });
    // eslint-disable-next-line
  }, []);

  const openSponsor = (e) => { setSponsorFor(e); setSponsorPlan(plans[0]?.id || ""); setSponsorName(e.sponsor_name || ""); };
  const paySponsor = async () => {
    if (!sponsorPlan) return toast.error("Seleziona un piano");
    setPaying(true);
    try {
      const { data } = await api.post(`/events/${sponsorFor.id}/sponsor`, { plan: sponsorPlan, sponsor_name: sponsorName, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e.id);
    setForm({ ...EMPTY, ...e, start_at: toLocal(e.start_at), end_at: toLocal(e.end_at), priority: e.priority || 0 });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.start_at) return toast.error("Titolo e data di inizio sono obbligatori");
    // In creazione geocodifica dall'indirizzo (lat/lng null); in modifica preserva le coordinate esistenti.
    const payload = { ...form, priority: parseInt(form.priority) || 0 };
    if (!editing) { payload.lat = null; payload.lng = null; }
    try {
      if (editing) await api.put(`/ceo/events/${editing}`, payload);
      else await api.post("/ceo/events", payload);
      setOpen(false); load();
      toast.success(editing ? "Evento aggiornato" : "Evento creato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    try { await api.delete(`/ceo/events/${id}`); load(); toast.success("Evento eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="ceo-events">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#00E676]" /> Eventi & Feste del territorio</h3>
          <p className="text-sm text-zinc-400 mt-1">Sagre, feste, concerti, mercatini, inaugurazioni ed eventi culturali sulla mappa del Canavese.</p>
        </div>
        <Button data-testid="event-new-btn" onClick={openNew} className="bg-[#00E676] text-black rounded-md"><Plus className="h-4 w-4 mr-1" /> Nuovo evento</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {events.map((e) => {
          const st = STATUS[e.status] || STATUS.upcoming;
          return (
            <div key={e.id} data-testid={`event-card-${e.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl overflow-hidden">
              {e.image ? <img src={e.image} alt={e.title} className="h-32 w-full object-cover" /> : <div className="h-32 w-full bg-gradient-to-br from-[#3B2A63] to-[#131316] flex items-center justify-center"><CalendarDays className="h-10 w-10 text-white/30" /></div>}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{e.title}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">{CAT_LABEL[e.category] || "Evento"} · {e.comune || e.location_name || "—"}</div>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${st.c}`}>{st.t}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-2">
                  <MapPin className="h-3 w-3" /> {e.lat != null ? "Localizzato" : "Nessuna coordinata"}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{e.start_at ? new Date(e.start_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}{e.end_at ? ` → ${new Date(e.end_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {e.featured && <span className="text-[10px] text-amber-300 bg-amber-400/15 border border-amber-400/30 rounded px-1.5 py-0.5 flex items-center gap-1"><Star className="h-3 w-3" /> In evidenza</span>}
                  {e.sponsored && <span className="text-[10px] text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-500/30 rounded px-1.5 py-0.5 flex items-center gap-1"><Megaphone className="h-3 w-3" /> Sponsor{e.sponsor_name ? `: ${e.sponsor_name}` : ""}</span>}
                  {e.sponsor_until && !e.sponsor_expired && <span className="text-[10px] text-cyan-300 bg-cyan-500/15 border border-cyan-500/30 rounded px-1.5 py-0.5">Attivo fino al {new Date(e.sponsor_until).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</span>}
                  {!e.published && <span className="text-[10px] text-zinc-400 bg-zinc-500/15 border border-zinc-500/30 rounded px-1.5 py-0.5">Bozza</span>}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="secondary" data-testid={`event-edit-${e.id}`} onClick={() => openEdit(e)}><Pencil className="h-3.5 w-3.5 mr-1" /> Modifica</Button>
                  <Button size="sm" data-testid={`event-sponsor-${e.id}`} onClick={() => openSponsor(e)} className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-md"><Rocket className="h-3.5 w-3.5 mr-1" /> Sponsorizza</Button>
                  <Button size="sm" variant="destructive" data-testid={`event-delete-${e.id}`} onClick={() => remove(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          );
        })}
        {events.length === 0 && <div className="col-span-full text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-xl">Nessun evento. Crea il primo evento del territorio.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131316] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica evento" : "Nuovo evento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo *</Label><Input data-testid="event-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Es. Sagra della Ranocchia" /></div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="event-category" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Descrizione</Label><Textarea data-testid="event-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Inizio *</Label><Input data-testid="event-start" type="datetime-local" value={form.start_at} onChange={(e) => set("start_at", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div><Label>Fine</Label><Input data-testid="event-end" type="datetime-local" value={form.end_at} onChange={(e) => set("end_at", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Luogo</Label><Input data-testid="event-location" value={form.location_name} onChange={(e) => set("location_name", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Piazza Vittorio" /></div>
              <div><Label>Comune</Label><Input data-testid="event-comune" value={form.comune} onChange={(e) => set("comune", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="San Maurizio Canavese" /></div>
            </div>
            <div><Label>Indirizzo (per localizzare sulla mappa)</Label><Input data-testid="event-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Via Roma 1" /></div>
            <div><Label>Immagine di copertina</Label><div className="mt-1"><ImageUpload testid="event-image" folder="events" value={form.image || ""} onChange={(url) => set("image", url)} /></div></div>
            <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2">
              <span className="text-sm flex items-center gap-2"><Star className="h-4 w-4 text-amber-300" /> In evidenza (home + mappa)</span>
              <Switch data-testid="event-featured" checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
            </div>
            <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2">
              <span className="text-sm flex items-center gap-2"><Megaphone className="h-4 w-4 text-fuchsia-300" /> Sponsorizzato (marker premium)</span>
              <Switch data-testid="event-sponsored" checked={form.sponsored} onCheckedChange={(v) => set("sponsored", v)} />
            </div>
            {form.sponsored && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Sponsor</Label><Input data-testid="event-sponsor-name" value={form.sponsor_name} onChange={(e) => set("sponsor_name", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Comune / Azienda" /></div>
                <div><Label>Priorità</Label><Input data-testid="event-priority" type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              </div>
            )}
            <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2">
              <span className="text-sm">Pubblicato</span>
              <Switch data-testid="event-published" checked={form.published} onCheckedChange={(v) => set("published", v)} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="event-save" onClick={save} className="bg-[#00E676] text-black rounded-md flex-1">{editing ? "Salva modifiche" : "Crea evento"}</Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annulla</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sponsorFor} onOpenChange={(v) => !v && setSponsorFor(null)}>
        <DialogContent className="bg-[#131316] border-white/10 text-white max-w-md" data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Rocket className="h-5 w-5 text-fuchsia-400" /> Sponsorizza l'evento</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400 -mt-1">Dai più visibilità a <b className="text-white">{sponsorFor?.title}</b> con un marker premium sulla mappa e la priorità in lista.</p>
          <div className="space-y-2 mt-2">
            {plans.map((p) => (
              <button
                key={p.id}
                data-testid={`sponsor-plan-${p.id}`}
                onClick={() => setSponsorPlan(p.id)}
                className={`w-full text-left rounded-xl border p-3 transition-colors ${sponsorPlan === p.id ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-white/10 bg-[#0A0A0C] hover:border-white/25"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold flex items-center gap-2">{sponsorPlan === p.id && <Check className="h-4 w-4 text-fuchsia-400" />}{p.name}</span>
                  <span className="font-bold text-[#00E676]">€{p.price.toFixed(2)}</span>
                </div>
                <div className="text-xs text-zinc-400 mt-1">{p.description}</div>
              </button>
            ))}
            {plans.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">Nessun piano disponibile.</div>}
          </div>
          <div className="mt-1">
            <Label>Nome sponsor (facoltativo)</Label>
            <Input data-testid="sponsor-name" value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Comune / Azienda / Pro Loco" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="sponsor-pay" onClick={paySponsor} disabled={paying || !sponsorPlan} className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-md flex-1">{paying ? "Reindirizzamento…" : "Paga e sponsorizza"}</Button>
            <Button variant="secondary" onClick={() => setSponsorFor(null)}>Annulla</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
